import React, {Fragment, useEffect, useState} from "react";
import {Layer, Rect, Stage, Text} from "react-konva";
import { useMediaQuery } from "react-responsive";
import {HallOne} from "./components/HallOne";
import { useIsMobile } from "./hooks/mobile";
import { API_Result } from "./interfaces/api";
import {Company} from "./interfaces/company";
import {API} from "./api";
import {HallTwo} from "./components/HallTwo";
import {ClickBox} from "./components/ClickBox";
import {message} from "antd";
import {CompanyDetails} from "./components/CompanyDetails";
import {Filter} from "./components/Filter";
import {FilterModel} from "./models/filter";
import {Branch} from "./interfaces/branch";
import {KonvaImage} from "./components/image";
import {Colors} from "./configs/colors";
import { PrepareCompany } from "./utils/company";
import Konva from "konva";
import Vector2d = Konva.Vector2d;


interface AppProps {
    logo?: boolean;
    zoom?: boolean;
}

const colorWhite = '#ffffff';
const colorBlue = '#0ea2ff';

export const App = ({logo, zoom}: AppProps) => {
    const isMobile = useIsMobile();
    const [ allCompanies, setAllCompanies ] = useState<Company[] | undefined>(undefined);
    const [ companies, setCompanies ] = useState<Company[] | undefined>(undefined);
    const [ company, setCompany ] = useState<Company | undefined>(undefined);
    const [ filter, setFilter ] = useState<FilterModel>(new FilterModel());
    const [ branches, setBranches ] = useState<Branch[]>([]);
    const [ width, setWidth ] = useState(window.innerWidth);
    const [ height, setHeight ] = useState(window.innerHeight);
    const [ scale, setScale ] = useState<Vector2d>({x: 1, y: 1});

    const stage = React.useRef<any>(null);

    const load = async () => {
        const mainAPI = await API.request<API_Result>("get", "/?rex-api-call=messe");
        if ( !mainAPI ) return message.error('API-Fehler');

        const preparedCompanies: any[] = [];
        for ( const slice of mainAPI.articleSlices ) {
            const tempComp = PrepareCompany(slice);
            if ( !tempComp ) continue;

            preparedCompanies.push(tempComp);
        }

        setCompanies([...preparedCompanies]);
        setAllCompanies([...preparedCompanies]);

        setBranches([...mainAPI.companyBranches]);
    }

    useEffect(() => {
        if ( !filter ) setFilter(new FilterModel());

        load();
        setCompanies([]);
        setAllCompanies([]);
    }, []);

    useEffect(() => {
        setWidth(window.innerWidth);
        setHeight(window.innerHeight);

        if ( stage.current ) {
            if ( isMobile ) {
                //stage.current.scale({ x: 0.3, y: 0.3 });
                setScale({x: 0.3, y: 0.3});
                stage.current.absolutePosition({x: width / 2 - 300, y: height / 2 - 200});
            } else {
                //stage.current.scale({ x: zoom ? 0.8 : 1, y: zoom ? 0.8 : 1 });
                setScale({ x: zoom ? 0.8 : 1, y: zoom ? 0.8 : 1 });
                stage.current.absolutePosition({x: width / 2 - 800, y: height / 2 - 200});
            }
        }
    }, [isMobile]);

    useEffect(() => {
        const handleResize = () => {
            setWidth(window.innerWidth);
            setHeight(window.innerHeight);

            if ( stage.current ) {
                if ( isMobile ) {
                    setScale({x: 0.3, y: 0.3});
                    stage.current.absolutePosition({x: width / 2 - 300, y: height / 2 - 200});
                } else {
                    setScale({ x: zoom ? 0.8 : 1, y: zoom ? 0.8 : 1 });
                    stage.current.absolutePosition({x: width / 2 - 800, y: height / 2 - 200});
                }
            }
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [isMobile, width, height]);

    const onCompanyClicked = (stand: string | number) => showCompany(stand);

    const showCompany = (stand: string | number) => {
        const company = companies!.find((c) => c.standNumber === `${stand}`);
        if ( !company ) return message.error(`Kein Unternehmen gefunden für Stand: ${stand}`);

        setCompany(company);
    }

    const getCompany = (stand: string | number): Company | undefined => {
        return companies!.find((comp) => comp.standNumber && comp.standNumber === `${stand}`);
    }

    const filterCompanies = (filter: FilterModel | undefined) => {
        let companiesCopy = [...allCompanies!];

        if ( !filter ) {
            setCompanies([...allCompanies!]);
            setFilter({ ...new FilterModel() });
            return;
        }

        if (filter.companies.length > 0) {
            companiesCopy = companiesCopy.filter((comp) => {
                return filter.companies.includes(comp.name!);
            });

            setCompanies([...companiesCopy]);
            setFilter({...filter});
            return;
        }

        if (filter.branches.length > 0) {
            companiesCopy = companiesCopy.filter((comp) => {
                return filter.branches.includes(comp.branche!);
            });
        }

        if (filter.careers.length > 0) {
            companiesCopy = companiesCopy.filter((comp) => {
                return comp.career!.some((career) => filter.careers.includes(career.name));
            });
        }

        if (filter.school) {
            companiesCopy = companiesCopy.filter((comp) => {
                return comp.career!.some((career) => career.type === "4")
            });
        }

        if (filter.studies) {
            companiesCopy = companiesCopy.filter((comp) => {
                return comp.career!.some((career) => career.type === "3" || career.type === "2")
            });
        }

        if (filter.educations) {
            companiesCopy = companiesCopy.filter((comp) => {
                return comp.career!.some((career) => career.type === "1")
            });
        }

        setCompanies([...companiesCopy]);
        setFilter({...filter});
    }

    const onSizeChange = (zoom: number) => {
        if ( zoom <= 0.1 ) return;
        if ( zoom >= 1.9 ) return;

        setScale({y: zoom, x: zoom});
    }

    return (<Fragment>
        { companies &&
            <Filter
                companies={companies}
                onChange={filterCompanies}
                branches={branches}
                filter={filter}
                scaleChange={onSizeChange}
                scale={scale}
            />
        }

        <Stage ref={stage} width={width - 10} height={height - 10} draggable scale={scale}>
            {/* Background Image */}
            <Layer>
                <KonvaImage x={-200} y={-400} width={2200} height={1100} src={"Background_JOBkLAHR.jpg"} />
            </Layer>

            <HallOne />
            {/* Überdachterbereich */}
            <Layer>

            </Layer>
            <HallTwo />

            {/* Hides logo only when specific url is called (for iFrame) */}
            {
                logo && <Layer>
                    <KonvaImage x={-160} y={-400} width={700} height={500} src={"https://jobklahr.de/resources/images/logo.svg"} />
                </Layer> 
            }


            {/* Company Boxes [HALLE 1]*/}
            { companies && <Layer>
                {/* Rechts */}
                <ClickBox x={550} y={130} stand={'S2'} company={getCompany} clicked={onCompanyClicked} />
                <ClickBox x={550} y={185} stand={'S1'} height={100} company={getCompany} clicked={onCompanyClicked} />
                <ClickBox x={550} y={290} height={100} stand={'AA'} company={getCompany} clicked={onCompanyClicked} />

                {/* Oben */}
                <ClickBox x={-20} y={130} stand={12} company={getCompany} clicked={onCompanyClicked} />
                <ClickBox x={35} y={130} stand={13} company={getCompany} clicked={onCompanyClicked} />
                <ClickBox x={90} y={130} stand={'S7'} company={getCompany} clicked={onCompanyClicked} />
                <ClickBox x={145} y={130} width={100} stand={'S6'} company={getCompany} clicked={onCompanyClicked} />
                <ClickBox x={250} y={130} width={100} stand={'S5'} company={getCompany} clicked={onCompanyClicked} />
                <ClickBox x={355} y={130} stand={'S4'} company={getCompany} clicked={onCompanyClicked}/>
                <ClickBox x={410} y={130} stand={'S3'} company={getCompany} clicked={onCompanyClicked} />

                {/* Unten */}
                <ClickBox x={355} y={430} width={100} stand={1} company={getCompany} clicked={onCompanyClicked} />
                <ClickBox x={250} y={430} width={100} stand={2} company={getCompany} clicked={onCompanyClicked} />
                <ClickBox x={195} y={430} stand={3} company={getCompany} clicked={onCompanyClicked} />
                <ClickBox x={140} y={430} stand={4} company={getCompany} clicked={onCompanyClicked} />
                <ClickBox x={35} y={430} width={100} stand={5} company={getCompany} clicked={onCompanyClicked} />
                <ClickBox x={-20} y={430} stand={6} company={getCompany} clicked={onCompanyClicked} />



                {/* Links */}
                <ClickBox x={-160} y={400} stand={7} company={getCompany} clicked={onCompanyClicked} />
                <ClickBox x={-160} y={295} height={100} width={50} stand={8} company={getCompany} clicked={onCompanyClicked} />
                <ClickBox x={-160} y={240} stand={9} company={getCompany} clicked={onCompanyClicked} />
                <ClickBox x={-160} y={185} stand={10} company={getCompany} clicked={onCompanyClicked} />
                <ClickBox x={-160} y={130} stand={11} company={getCompany} clicked={onCompanyClicked} />


                <ClickBox x={355} y={250} width={104} height={104} stand={14} company={getCompany} clicked={onCompanyClicked} />
                <ClickBox x={250} y={305} width={100} stand={15} company={getCompany} clicked={onCompanyClicked} />

                {/* Mitte */}
                <ClickBox x={195} y={305} stand={16} company={getCompany} clicked={onCompanyClicked} />
                <ClickBox x={85} y={305} width={104} stand={17} company={getCompany} clicked={onCompanyClicked} />
                <ClickBox x={-20} y={305} width={100} stand={18} company={getCompany} clicked={onCompanyClicked} />
                <ClickBox x={-20} y={250} width={100} stand={19} company={getCompany} clicked={onCompanyClicked} />
                <ClickBox x={85} y={250} stand={20} company={getCompany} clicked={onCompanyClicked} />

                <ClickBox x={140} y={250} stand={21} company={getCompany} clicked={onCompanyClicked} />
                <ClickBox x={195} y={250} stand={22} company={getCompany} clicked={onCompanyClicked} />
                <ClickBox x={250} y={250} width={100} stand={23} company={getCompany} clicked={onCompanyClicked} />
                


            </Layer> }

            {/* Company Boxes [HALLE 2]*/}
            { companies && <Layer>
                {/* Unten */}
                <ClickBox x={1220} y={125} stand={24} width={100} company={getCompany} clicked={onCompanyClicked} />
                <ClickBox x={1325} y={125} stand={25} width={100} company={getCompany} clicked={onCompanyClicked} />
                <ClickBox x={1430} y={125} stand={26} width={100} company={getCompany} clicked={onCompanyClicked} />
                <ClickBox x={1535} y={125} stand={27} company={getCompany} clicked={onCompanyClicked} />
                <ClickBox x={1590} y={125} stand={28} company={getCompany} clicked={onCompanyClicked} />
                <ClickBox x={1645} y={125} width={100} stand={29} company={getCompany} clicked={onCompanyClicked} />
                <ClickBox x={1750} y={125} stand={30} company={getCompany} clicked={onCompanyClicked} />

                {/* Rechts */}
                <ClickBox x={1810} y={-10} height={100} stand={31} company={getCompany} clicked={onCompanyClicked} />
                <ClickBox x={1810} y={-115} height={100} stand={32} company={getCompany} clicked={onCompanyClicked} />
                <ClickBox x={1810} y={-170} stand={33} company={getCompany} clicked={onCompanyClicked} />

                {/* Oben */}
                <ClickBox x={1740} y={-225} stand={34} company={getCompany} clicked={onCompanyClicked} />
                <ClickBox x={1635} y={-225} stand={35} width={100} company={getCompany} clicked={onCompanyClicked} />
                <ClickBox x={1580} y={-225} stand={36} company={getCompany} clicked={onCompanyClicked} />
                <ClickBox x={1525} y={-225} stand={37} company={getCompany} clicked={onCompanyClicked} />
                <ClickBox x={1470} y={-225} stand={38} company={getCompany} clicked={onCompanyClicked} />
                <ClickBox x={1415} y={-225} stand={39} company={getCompany} clicked={onCompanyClicked} />
                <ClickBox x={1360} y={-225} stand={40} company={getCompany} clicked={onCompanyClicked} />
                <ClickBox x={1305} y={-225} stand={41} company={getCompany} clicked={onCompanyClicked} />
                <ClickBox x={1200} y={-225} stand={42} width={100} company={getCompany} clicked={onCompanyClicked} />

                {/* Mitte */}

                <ClickBox x={1200} y={-81} height={103} width={103} stand={47} company={getCompany} clicked={onCompanyClicked} />

                <ClickBox x={1307} y={-29} stand={48} width={102} company={getCompany} clicked={onCompanyClicked} />
                <ClickBox x={1412} y={-29} stand={49} company={getCompany} clicked={onCompanyClicked} />
                <ClickBox x={1467} y={-29} stand={50} company={getCompany} clicked={onCompanyClicked} />
                <ClickBox x={1522} y={-29} width={100} stand={51} company={getCompany} clicked={onCompanyClicked} />
                <ClickBox x={1627} y={-29} stand={52} company={getCompany} clicked={onCompanyClicked} />
                <ClickBox x={1682} y={-81} height={103} stand={53} company={getCompany} clicked={onCompanyClicked} />

                <ClickBox x={1578} y={-82} width={100} stand={54} company={getCompany} clicked={onCompanyClicked} />
                <ClickBox x={1522} y={-82} width={52} stand={55} company={getCompany} clicked={onCompanyClicked} />
                <ClickBox x={1465} y={-82} width={52} stand={56} company={getCompany} clicked={onCompanyClicked} />
                <ClickBox x={1410} y={-82} width={52} stand={57} company={getCompany} clicked={onCompanyClicked} />
                <ClickBox x={1355} y={-82} width={52} stand={58} company={getCompany} clicked={onCompanyClicked} />
                <ClickBox x={1306} y={-82} width={47} stand={59} company={getCompany} clicked={onCompanyClicked} />



                {/* Links */}
                <ClickBox x={1110} y={-170} stand={43} company={getCompany} clicked={onCompanyClicked} />
                <ClickBox x={1110} y={-115} height={100} stand={44} company={getCompany} clicked={onCompanyClicked} />
                <ClickBox x={1110} y={-10} stand={45} company={getCompany} clicked={onCompanyClicked} />
                <ClickBox x={1110} y={45} stand={46} company={getCompany} clicked={onCompanyClicked} />


            </Layer> }
        </Stage>

        {/*if company is selected by clicking on box*/}
        {company && <CompanyDetails company={company} onClose={() => setCompany(undefined)} /> }
    </Fragment>);
}
